/**
 * Theme utility for managing light/dark theme
 * Handles data-theme attribute on html element and localStorage persistence
 */

const THEME_STORAGE_KEY = 'app-theme';
const DEFAULT_THEME = 'light';
const themeListeners = new Set();

/**
 * Get current theme from localStorage, HTML attribute, or default
 * @returns {string} 'light' or 'dark'
 */
export function getTheme() {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') {
      return stored;
    }
    
    // Fallback to HTML attribute if localStorage is empty
    const html = document.documentElement;
    const htmlTheme = html.getAttribute('data-theme');
    if (htmlTheme === 'dark' || htmlTheme === 'light') {
      return htmlTheme;
    }
    
    return DEFAULT_THEME;
  } catch (e) {
    return DEFAULT_THEME;
  }
}

/**
 * Set theme and update DOM
 * @param {string} theme - 'light' or 'dark'
 */
export function setTheme(theme) {
  const html = document.documentElement;
  const validTheme = theme === 'dark' ? 'dark' : DEFAULT_THEME;
  
  html.setAttribute('data-theme', validTheme);
  
  try {
    localStorage.setItem(THEME_STORAGE_KEY, validTheme);
  } catch (e) {
    console.warn('Failed to save theme to localStorage:', e);
  }

  notifyThemeChange(validTheme);
}

/**
 * Toggle between light and dark theme
 * @returns {string} The new theme value
 */
export function toggleTheme() {
  const currentTheme = getTheme();
  const newTheme = currentTheme === 'light' ? 'dark' : 'light';
  setTheme(newTheme);
  return newTheme;
}

/**
 * Initialize theme on app load
 */
export function initTheme() {
  const theme = getTheme();
  setTheme(theme);
}

/**
 * Build a theme-aware image path when using /images/light/ as the base
 * @param {string} src
 * @param {string} theme
 * @returns {string}
 */
export function themedImage(src, theme) {
  if (!src || typeof src !== 'string') {
    return src;
  }

  if (src.startsWith('/images/light/')) {
    return src.replace('/images/light/', `/images/${theme}/`);
  }

  if (src.startsWith('images/light/')) {
    return src.replace('images/light/', `images/${theme}/`);
  }

  return src;
}

/**
 * Subscribe to theme changes, returns an unsubscribe function.
 * @param {Function} listener
 * @returns {Function}
 */
export function onThemeChange(listener) {
  if (typeof listener !== 'function') {
    return () => {};
  }

  themeListeners.add(listener);
  ensureThemeObserver();

  return () => {
    themeListeners.delete(listener);
  };
}

function notifyThemeChange(theme) {
  themeListeners.forEach(listener => listener(theme));
}

function ensureThemeObserver() {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
    return;
  }

  if (ensureThemeObserver.observer) {
    return;
  }

  const html = document.documentElement;
  if (!html) {
    return;
  }

  ensureThemeObserver.observer = new MutationObserver(() => {
    notifyThemeChange(getTheme());
  });

  ensureThemeObserver.observer.observe(html, {
    attributes: true,
    attributeFilter: ['data-theme']
  });
}

